import { ChevronLeft, ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import type { MissionListPagination } from "@/types/missions"

/**
 * OKS-31 — Brand Mission list pagination footer.
 *
 * Hybrid pattern (PRD Open-Q3 + UX Spec §5):
 *   - Desktop ≥768px: numbered pager — `Showing X–Y of Z · ‹ 1 2 3 ›`.
 *   - Mobile <768px: "Load more" button (advances `page` by 1, server
 *     returns the next 25; FE concatenates rows). The next-button
 *     disables at the last page.
 *
 * Pagination shape mirrors `Candidates::Index` / `Crew::Index`
 * (`{page, pages, count, prev, next}` — Pagy 9 5-key projection).
 */
interface MissionListPaginationFooterProps {
  pagination: MissionListPagination
  perPage?: number
  onPageChange: (page: number) => void
  className?: string
}

export function MissionListPaginationFooter({
  pagination,
  perPage = 25,
  onPageChange,
  className,
}: MissionListPaginationFooterProps) {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { page, pages, count } = pagination

  if (count === 0) {
    return (
      <div
        data-testid="mission-list-pagination"
        className={cn(
          "text-muted-foreground flex justify-center py-4 text-sm",
          className,
        )}
      >
        {t("missions.index.pagination.summary", { from: 0, to: 0, count: 0 })}
      </div>
    )
  }

  const from = (page - 1) * perPage + 1
  const to = Math.min(page * perPage, count)
  const isLast = page >= pages
  const isFirst = page <= 1

  if (isMobile) {
    return (
      <nav
        data-testid="mission-list-pagination"
        aria-label={t("missions.index.pagination.aria_label")}
        className={cn("flex flex-col items-center gap-2 py-4", className)}
      >
        <p className="text-muted-foreground text-sm tabular-nums">
          {t("missions.index.pagination.summary", { from, to, count })}
        </p>
        {!isLast && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            data-testid="mission-list-pagination-load-more"
          >
            {t("missions.index.pagination.load_more")}
          </Button>
        )}
      </nav>
    )
  }

  // Desktop — numbered pager. Show all pages when ≤7; otherwise an
  // ellipsised window: 1 … current-1, current, current+1 … pages.
  const pageList = compactPageList(page, pages)

  return (
    <nav
      data-testid="mission-list-pagination"
      aria-label={t("missions.index.pagination.aria_label")}
      className={cn(
        "flex flex-col items-center gap-3 py-4 sm:flex-row sm:justify-between",
        className,
      )}
    >
      <p className="text-muted-foreground text-sm tabular-nums">
        {t("missions.index.pagination.summary", { from, to, count })}
      </p>
      <ul className="flex items-center gap-1">
        <li>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isFirst}
            aria-disabled={isFirst}
            onClick={() => onPageChange(page - 1)}
            data-testid="mission-list-pagination-prev"
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
            <span className="sr-only">
              {t("missions.index.pagination.prev_aria")}
            </span>
          </Button>
        </li>
        {pageList.map((item, idx) =>
          item === "ellipsis" ? (
            <li
              key={`ellipsis-${idx}`}
              className="text-muted-foreground px-1 text-sm"
              aria-hidden="true"
            >
              …
            </li>
          ) : (
            <li key={item}>
              <Button
                type="button"
                variant={item === page ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(item)}
                aria-current={item === page ? "page" : undefined}
                data-testid={`mission-list-pagination-page-${item}`}
              >
                {item}
              </Button>
            </li>
          ),
        )}
        <li>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isLast}
            aria-disabled={isLast}
            onClick={() => onPageChange(page + 1)}
            data-testid="mission-list-pagination-next"
          >
            <ChevronRight className="size-4" aria-hidden="true" />
            <span className="sr-only">
              {t("missions.index.pagination.next_aria")}
            </span>
          </Button>
        </li>
      </ul>
    </nav>
  )
}

/**
 * Compact page list with ellipsis. Always shows current ± 1, plus
 * the first and last pages — middle is collapsed when total > 7.
 */
function compactPageList(
  current: number,
  total: number,
): (number | "ellipsis")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  const list: (number | "ellipsis")[] = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)

  if (start > 2) list.push("ellipsis")
  for (let p = start; p <= end; p += 1) list.push(p)
  if (end < total - 1) list.push("ellipsis")

  list.push(total)
  return list
}
